import { useTypedSelector } from 'hooks';
import {
    selectMerchantsUserBelongs,
    selectMorsUserBelongs
} from 'features/users/selectors';
import useUserActions from 'features/users/useActions';
import { fromPaginateResourceResponse } from 'util/api';
import type { ID, Option } from 'types';
import type {
    User,
    UsersMerchantsPivot,
    UsersMerchantsResponse,
    UsersMorsPivot,
    UsersMorsResponse
} from 'features/users/types';

type UpdateAssociationArgs = {
    readonly user: User;
    readonly associations: Option[];
};

const toKeys = (ids: Array<unknown>) => new Set(ids.map(String));

/**
 * Splits current vs. requested associations into what has to be removed and added,
 * so unchanged pivots are left alone (re-creating them fails on the API).
 */
const diffAssociations = <T>(current: Array<T>, requested: Array<T>) => {
    const currentKeys = toKeys(current);
    const requestedKeys = toKeys(requested);

    return {
        toRemove: current.filter(id => !requestedKeys.has(String(id))),
        toAdd: requested.filter(id => !currentKeys.has(String(id)))
    };
};

export const useUserAssociations = (user: Partial<User>) => {
    const {
        associateUserMerchant,
        associateUserMor,
        disassociateUserMerchant,
        disassociateUserMor,
        getUsersMerchants,
        getUsersMors
    } = useUserActions();

    // Fallbacks when a fresh read is skipped because one is already in flight
    const merchantIds: Array<ID> = useTypedSelector(selectMerchantsUserBelongs(user.coreId));
    const mors: Array<string> = useTypedSelector(selectMorsUserBelongs(user.coreId));

    const getCurrentMerchantIds = async (userId: ID) => {
        const [request] = getUsersMerchants(userId);
        const response = await request as UsersMerchantsResponse | undefined;

        return response
            ? fromPaginateResourceResponse<UsersMerchantsPivot>(response)
                .map(({ merchantId }) => merchantId)
            : merchantIds;
    };

    const getCurrentMors = async (userId: ID) => {
        const [request] = getUsersMors(userId);
        const response = await request as UsersMorsResponse | undefined;

        return response
            ? fromPaginateResourceResponse<UsersMorsPivot>(response)
                .map(({ MOR }) => MOR)
            : mors;
    };

    const updateUserMerchantAssosiation = async ({
        user,
        associations
    }: UpdateAssociationArgs) => {
        const { toRemove, toAdd } = diffAssociations<ID>(
            await getCurrentMerchantIds(user.coreId),
            [associations].flat().map(({ id }) => id)
        );

        for (const merchantId of toRemove) {
            const [request] = disassociateUserMerchant({
                userId: user.coreId,
                merchantId
            });

            await request;
        }

        for (const merchantId of toAdd) {
            const [request] = associateUserMerchant({
                userId: user.coreId,
                merchantId
            });

            await request;
        }
    };

    const updateUserMorAssociation = async ({
        user,
        associations
    }: UpdateAssociationArgs) => {
        const { toRemove, toAdd } = diffAssociations<string>(
            await getCurrentMors(user.coreId),
            [associations].flat().map(({ id }) => String(id))
        );

        for (const MOR of toRemove) {
            const [request] = disassociateUserMor({
                userId: user.coreId,
                MOR
            });

            await request;
        }

        for (const MOR of toAdd) {
            const [request] = associateUserMor({
                userId: user.coreId,
                MOR
            });

            await request;
        }
    };

    return {
        updateUserMerchantAssosiation,
        updateUserMorAssociation
    };
};

export default useUserAssociations;
